/**
 * Tools → Integrity. The watchdog's own surface.
 *
 * WHY THIS PAGE EXISTS
 * `integrityReport()` has computed sixteen checks for a while, and four of them, non-OK only,
 * were rendered inside the notification bell. Two consequences the owner hit on 2026-08-03:
 * he could not find the watchdog when asked to read it, and the capital reconciliation
 * vanished from the UI at the exact moment it started passing — because OK checks are
 * filtered out of an action list by definition.
 *
 * A CHECK THAT PASSES STILL HAS A MARGIN. The residual was -$1,730 and outside the band at
 * 09:00 and -$102.92 inside it by 17:00. Both of those are "passing or not passing", one bit;
 * 7% of the band and 95% of it are the same bit and completely different situations. This
 * page renders the margin, not the bit.
 *
 * COLOUR IS INHERITED, NOT CHOSEN. Tools is a red section — History, Settings and Tax all
 * run #FF4D6A through the h1 gradient, the hero --hx, the ring and the nav dot — so this page
 * does too. What stays semantic is the DATA: green for a check that passes, red for one that
 * does not, amber for one nobody can answer, and the band's green centre with red edges. Tax
 * draws exactly the same line, with a red section identity and a green/red receipt rail.
 *
 * ADDITIVE. The bell is untouched and still shows its four-item summary. Nothing here writes,
 * fetches, or calls an LLM — it is one pure read of state, same as integrityReport itself.
 * Every shell it uses (neo-hero, grid4/kpi, tax-pr, gls-miles, mny-row, jrn-more) already
 * exists; the only new CSS is the two-sided tolerance band, because the Tax receipt rail
 * answers a one-sided question and a signed residual is not one.
 */

import { S, _lastTrims, _IMPORT_KEYS, MERGE_ARRAYS } from '../core/state.js';
import { netWorthUSD, _nwProviderWired } from '../core/risk-calc.js';
import { integrityReport } from '../core/integrity.js';
import { evaluateTrigger } from '../core/portfolio-policy.js';
import { lotEchoes } from '../core/month-lots.js';
import { evaluateGates, GATES } from '../core/cycle-phase.js';
import { reconcileCapital } from '../core/capital.js';
import { fxEffectOnCost } from '../core/fx-effect.js';
import { unknownFeeHoldings, unknownFeeTrades } from '../core/fees.js';
import { capitalCtx } from './dashboard.js';
import { esc } from '../core/utils.js';
import { taxRefundsPaid, taxSnapshotsSeen } from '../core/tax-bridge.js';
import { statementsSeen } from '../core/statement-bridge.js';

const OPEN = new Set();

const AC = { critical: 'var(--red)', warn: 'var(--yel)', unknown: 'var(--ora)', ok: 'var(--grn)' };
const MARK = { critical: '!', warn: '!', unknown: '?', ok: '✓' };
const MILE_CLS = { critical: 'miss', warn: 'miss', unknown: 'cur', ok: 'done' };

const _f0 = n => (n == null || !isFinite(n)) ? '—' : (n < 0 ? '−' : '') + '$' + Math.abs(n).toLocaleString('en-US', { maximumFractionDigits: 0 });
const _f2 = n => (n == null || !isFinite(n)) ? '—' : (n < 0 ? '−' : '') + '$' + Math.abs(n).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

/** Short id shown at the head of each row, so a check can be named in conversation. */
/**
 * Short id per check, for the milestone chips.
 *
 * A missing entry does not fail — it falls through to the full id, which on 2026-08-04 put
 * `cashflow.recurring` and `plan.currentmonth` on top of each other in a row sized for two
 * characters. A map that silently degrades is the same shape as every other defect in this
 * codebase, so integrity-page.test.js now asserts that every id integrity.js can emit has an
 * entry here, and _rid() clips anything that still slips through instead of overflowing.
 */
const RID = {
  'capital.reconciliation': 'C1', 'capital.unclassified': 'C2', 'netWorth.singleSource': 'N1',
  'fx.rate': 'X3', 'fx.age': 'X4', 'fx.costBasisDates': 'X1', 'fx.effect': 'X2',
  'fees.model': 'F1', 'fees.trade-exit': 'F2', 'retention.trims': 'R1', 'plan.contributions': 'E1',
  'prices.missing': 'P1', 'prices.stale': 'P2', 'trades.unpriced': 'P3', 'reserve.unpriced': 'P4',
  'signals.age': 'S1', 'snapshots.gap': 'W1', 'drift.age': 'D1',
  'ai.scan': 'A1', 'ai.impact': 'A2', 'ai.ta': 'A3', 'ai.fg': 'A4', 'ai.insights': 'A5',
  'cashflow.recurring': 'M1', 'plan.currentMonth': 'M2', 'cashflow.commitmentPlan': 'M3', 'plan.lotEcho': 'M4', 'cycle.brakes': 'G5', 'cash.noNegative': 'B4', 'lot.dripOnCash': 'B5', 'cash.fxPurpose': 'B6', 'capital.lotBasisGap': 'A6', 'cashflow.incomeOverflow': 'M5', 'cashflow.statementMismatch': 'M6',
  'cash.bankSplit': 'B1', 'cash.reconcileAge': 'B2', 'cash.ledgerMatch': 'B3', 'capital.declaredVsWired': 'C3',
  'positions.cutNotExited': 'H1', 'trades.beyondStop': 'H2', 'tax.refundBooked': 'T1', 'tax.snapshotAge': 'T2', 'portfolio.mandate': 'G1', 'portfolio.sizing': 'G3', 'portfolio.accumulateTrigger': 'G4', 'import.keyLoss': 'G2', 'data.idsDistinct': 'I1',
};

/** Never wider than the chip. An unmapped id is clipped, not spilled across its neighbour. */
function _rid(id) {
  if (RID[id]) return RID[id];
  const tail = String(id || '?').split('.').pop();
  return (tail.length > 6 ? tail.slice(0, 5) + '…' : tail).toLowerCase();
}

export function toggleIntegrityRow(id) {
  if (OPEN.has(id)) OPEN.delete(id); else OPEN.add(id);
  renderIntegrity();
}

function _row(c) {
  const open = OPEN.has(c.id);
  const detail = [
    c.consequence ? esc(c.consequence) : '',
    c.remedy ? '<div style="margin-top:10px;color:var(--t3);font-family:var(--mono);font-size:11px">' + esc(c.remedy) + '</div>' : '',
  ].filter(Boolean).join('') || '&nbsp;';
  return '<div class="mny-row' + (open ? ' open' : '') + '" style="--ac:' + (AC[c.status] || 'var(--cyan)') + '">' +
    '<div class="mny-rmain" data-action="toggleIntegrityRow" data-args=\'["' + c.id + '"]\'>' +
      '<span class="dot"></span>' +
      '<span class="itg-rid">' + esc(_rid(c.id)) + '</span>' +
      '<span class="rt" style="font-weight:500;color:var(--t2)">' + esc(c.label) + '</span>' +
      '<span class="rsum">' + esc(c.detail || '') + '</span>' +
      '<span class="chev">▾</span>' +
    '</div>' +
    '<div class="mny-rdetail"><div class="mny-rinner">' + detail + '</div></div></div>';
}

/**
 * Where the pin sits, and whether it is off the scale.
 *
 * The band runs from -tolerance to +tolerance. A residual beyond an edge is CLAMPED to it
 * and flagged, because "off the scale" must still render and must not render as though it
 * were merely at the edge — that is the difference between a check that is about to fail and
 * one that already has.
 *
 * Exported so the arithmetic is testable without a DOM and without a state fixture that
 * happens to produce an out-of-band residual.
 */
export function bandPosition(residualUSD, tolerance) {
  if (!(tolerance > 0) || residualUSD == null || !isFinite(residualUSD)) return null;
  const ratio = Math.max(-1, Math.min(1, residualUSD / tolerance));
  return {
    pct: 50 + ratio * 50,
    over: Math.abs(residualUSD) > tolerance,
    usedPct: Math.round(Math.abs(residualUSD) / tolerance * 100),
  };
}

function _band(cap) {
  const tol = cap && cap.tolerance > 0 ? cap.tolerance : null;
  const res = cap ? cap.residualUSD : null;
  if (tol == null || res == null || !isFinite(res)) {
    return '<div class="itg-band"><div class="bandbg"></div>' +
      '<div class="lab" style="left:50%">not computed</div></div>';
  }
  const { pct, over, usedPct: used } = bandPosition(res, tol);
  return '<div class="itg-band">' +
    '<div class="bandbg"></div>' +
    '<div class="edge" style="left:0"></div>' +
    '<div class="edge" style="left:calc(100% - 2px)"></div>' +
    '<div class="zero"></div>' +
    '<div class="cur' + (over ? ' over' : '') + '" style="left:' + pct.toFixed(1) + '%"></div>' +
    '<div class="lab e" style="left:0;transform:none">−' + _f0(tol) + '</div>' +
    '<div class="lab" style="left:50%">0</div>' +
    '<div class="lab ' + (over ? 'e' : 'c') + '" style="left:' + pct.toFixed(1) + '%;top:-16px">' + _f2(res) + '</div>' +
    '<div class="lab e" style="left:100%;transform:translateX(-100%)">+' + _f0(tol) + '</div>' +
    '<span data-used="' + used + '"></span></div>';
}

export function renderIntegrity() {
  const host = document.getElementById('itgBody');
  if (!host) return;

  // Computed exactly the way the Dashboard computes it, so the two can never disagree about
  // the same state. A failure degrades to a report without capital rather than an empty page.
  let cap = null, fx = null, fees = null;
  try {
    const ctx = capitalCtx();
    cap = reconcileCapital(S, ctx);
    fx = fxEffectOnCost(S, ctx);
  } catch (e) { console.warn('[Integrity] capital/fx not computable:', e && e.message); }
  try { fees = unknownFeeHoldings(S.holdings); } catch (_e) { fees = null; }
  let feeTrades = null;
  try { feeTrades = unknownFeeTrades(S.trades); } catch (_e) { feeTrades = null; }

  // `netWorth.singleSource` compares the number risk-calc hands every consumer against the
  // canonical one. Reached through `window.__computeTW` rather than an import, for the same
  // reason `goals.js` uses the injection: `dashboard.js -> ... -> render-loop.js` is a cycle
  // (trap #12). Absent => the check reports UNKNOWN, never OK.
  let netWorth = null;
  try {
    const _canon = (typeof window !== 'undefined' && window.__computeTW) ? window.__computeTW() : null;
    if (typeof _canon === 'number' && isFinite(_canon)) {
      netWorth = { canonical: _canon, viaRiskCalc: netWorthUSD(), providerWired: _nwProviderWired() };
    }
  } catch (_e) { netWorth = null; }

  const rep = integrityReport(S, Date.now(), { capital: cap, fx, fees, feeTrades, trims: _lastTrims, taxRefunds: taxRefundsPaid(), taxSnapshots: taxSnapshotsSeen(), statements: statementsSeen(), importKeys: _IMPORT_KEYS, mergeArrays: MERGE_ARRAYS, netWorth, evaluateTrigger, lotEchoes, cycleGates: evaluateGates, cycleInvalidations: GATES.invalidations });
  const checks = rep.checks || [];
  const attn = checks.filter(c => c.status !== 'ok');
  const pass = checks.filter(c => c.status === 'ok');

  const tol = cap && cap.tolerance > 0 ? cap.tolerance : null;
  const res = cap ? cap.residualUSD : null;
  const usedPct = (tol && res != null && isFinite(res)) ? Math.round(Math.abs(res) / tol * 100) : null;
  const unusedPct = usedPct == null ? null : Math.max(0, 100 - usedPct);
  const ringPct = checks.length ? pass.length / checks.length : 0;

  const meta = document.getElementById('itgMeta');
  if (meta) meta.textContent = checks.length + ' checks · ' + attn.length + ' need attention';

  const chip = (l, v, col) => '<span class="neo-chip">' + l + ' <b' + (col ? ' style="color:' + col + '"' : '') + '>' + v + '</b></span>';
  const kpi = (l, v, col, sub) => '<div class="panel kpi"><div class="lbl">' + l + '</div>' +
    '<div class="v" style="color:' + col + '">' + v + '</div><div class="ksub">' + sub + '</div></div>';

  const undated = fx && fx.undated ? fx.undated.length : null;
  const cov = fx && fx.coveragePct != null ? Math.round(fx.coveragePct) : null;
  const nUnknownFee = fees ? fees.length : null;
  const heldTickers = (S.holdings || []).filter(h => h && (h.qty || 0) > 0).length;

  host.innerHTML =
    '<div class="neo-hero" style="--hx:#FF4D6A;border-color:rgba(255,77,106,.35)">' +
      '<div class="neo-hleft">' +
        '<div class="neo-hlbl">CAPITAL RECONCILIATION · RESIDUAL · UNATTRIBUTED</div>' +
        '<div class="neo-hnum">' + (res == null ? 'not computed' : _f2(res)) +
          (tol ? ' <span style="color:var(--t3);font-weight:400">/ ±' + _f0(tol) + '</span>' : '') + '</div>' +
        '<div class="neo-chips">' +
          chip('Assets at cost', cap ? _f0(cap.assetsAtCostUSD) : '—') +
          chip('Explained', cap ? _f0(cap.explainedUSD) : '—') +
          (usedPct != null ? chip('Band used', usedPct + '%', usedPct > 100 ? 'var(--red)' : usedPct > 60 ? 'var(--yel)' : 'var(--grn)') : '') +
          (cap && cap.unknown && cap.unknown.length ? chip('Unclassified', cap.unknown.length, 'var(--red)') : '') +
        '</div>' +
      '</div>' +
      '<div class="neo-ring"><svg width="124" height="124" viewBox="0 0 124 124">' +
        '<defs><linearGradient id="itgGradR" x1="0" y1="0" x2="1" y2="1">' +
        '<stop offset="0%" stop-color="#FF4D6A"/><stop offset="100%" stop-color="#ff8c42"/></linearGradient></defs>' +
        '<circle class="bgc" cx="62" cy="62" r="51.4"/>' +
        '<circle class="fgc" cx="62" cy="62" r="51.4" style="stroke:url(#itgGradR);stroke-dasharray:323;stroke-dashoffset:' +
          (323 * (1 - ringPct)).toFixed(1) + '"/></svg>' +
        '<div class="rlbl"><b style="color:#ff7a95">' + pass.length + '/' + checks.length + '</b><span>PASSING</span></div></div>' +
    '</div>' +

    '<div class="grid4">' +
      kpi('Cost Basis Dated', cov == null ? '—' : cov + '%', cov === 100 ? 'var(--grn)' : 'var(--yel)',
        undated == null ? 'not computed' : undated + ' undated item(s)') +
      kpi('Fee Models', nUnknownFee == null ? '—' : (heldTickers - nUnknownFee) + '/' + heldTickers,
        nUnknownFee ? 'var(--ora)' : 'var(--grn)',
        nUnknownFee ? fees.slice(0, 3).map(f => f.ticker).join(', ') + ' unverified' : 'every venue measured') +
      kpi('Unclassified Cash', cap ? String((cap.unknown || []).length) : '—',
        cap && (cap.unknown || []).length ? 'var(--red)' : 'var(--grn)',
        'of ' + ((S.cashTxs || []).length) + ' ledger rows') +
      kpi('Schema', 'v' + (S._schemaVersion || '?'), '#ff7a95', 'migrations applied on load') +
    '</div>' +

    '<div class="panel" style="margin-bottom:18px">' +
      '<h2 class="pt" style="margin-bottom:6px">Tolerance band ' +
        '<span class="itg-hint">· a check that passes still has a margin, and this is it</span></h2>' +
      '<div class="tax-pr">' +
        '<div class="tax-pid"><div class="t">Residual</div><div class="s">' +
          (cap ? (cap.reconciled ? 'inside band' : 'OUTSIDE') : 'unknown') + '</div></div>' +
        _band(cap) +
        '<div class="itg-stat"><div class="big' + (unusedPct != null && unusedPct > 0 ? ' pos' : '') + '">' +
          (unusedPct == null ? '—' : unusedPct + '%') + '</div>' +
          '<div class="sm">of band unused</div>' +
          '<div class="sm">tolerance = 2% of assets at cost</div></div>' +
      '</div>' +
      '<div class="gls-miles" style="margin-top:14px;padding-top:14px;border-top:1px solid rgba(255,255,255,.04)">' +
        checks.map(c => '<div class="gls-mile ' + (MILE_CLS[c.status] || 'lock') + '" title="' + esc(c.label) + '">' +
          '<div class="c">' + (MARK[c.status] || '·') + '</div><span>' + esc(_rid(c.id).toLowerCase()) + '</span></div>').join('') +
      '</div>' +
      '<div class="itg-hint" style="text-align:center;display:block;margin-top:8px;font-family:var(--mono)">' +
        pass.length + ' passing · ' +
        '<span style="color:var(--ora)">' + checks.filter(c => c.status === 'unknown').length + ' unknown</span> · ' +
        '<span style="color:var(--red)">' + checks.filter(c => c.status !== 'ok' && c.status !== 'unknown').length + ' need attention</span></div>' +
    '</div>' +

    '<div class="mny-qhd">CHECKS · NOT PASSING FIRST</div>' +
    (attn.length ? attn.map(_row).join('')
      : '<div class="mny-row" style="--ac:var(--grn)"><div class="mny-rmain"><span class="dot"></span>' +
        '<span class="rt" style="color:var(--t2)">Nothing needs attention</span>' +
        '<span class="rsum">all ' + checks.length + ' checks pass</span></div></div>') +
    (pass.length ? '<details class="jrn-more"><summary>' + pass.length +
      ' passing — each with its margin</summary><div style="padding-top:10px">' +
      pass.map(_row).join('') + '</div></details>' : '');
}
